import { AfterCloseCommands, type AfterCloseCommandSession } from "./after-close-service.ts";
import { createContext, useContext, useMemo, useRef, type ReactNode } from "react";
import { Effect, Scope } from "effect";
import { Atom, AsyncResult } from "effect/unstable/reactivity";
import { useAtomMount, useAtomValue, useAtomSet } from "@effect/atom-react";
import { CommandRegistry } from "./service.ts";
import type { SurfaceCommand } from "./model.ts";
import type { FrontendRoot } from "../state/root-owner.ts";

export interface CommandRoot {
  readonly atoms: FrontendRoot<CommandRegistry | AfterCloseCommands>["atoms"];
  readonly commands: import("effect").Context.Service.Shape<typeof CommandRegistry>;
}
const CommandsContext = createContext<CommandRoot | undefined>(undefined);
export const CommandsProvider = ({ value, children }: { readonly value: CommandRoot; readonly children: ReactNode }) => <CommandsContext.Provider value={value}>{children}</CommandsContext.Provider>;
export function useAfterCloseCommand(open: boolean) {
  const root = useContext(CommandsContext), atoms = root?.atoms, state = useRef(open); state.current = open;
  const identity = useMemo(() => ({ atoms }), [atoms]), latest = useRef(identity); latest.current = identity;
  const opened = useMemo<Atom.Atom<AsyncResult.AsyncResult<{ readonly identity: object; readonly session: AfterCloseCommandSession; readonly owner: Scope.Scope } | undefined>>>(() => atoms
    ? atoms.atom(Effect.gen(function* () { const owner = yield* Scope.Scope; const session = yield* (yield* AfterCloseCommands).open(() => latest.current === identity && !state.current); return { identity, session, owner }; })).pipe(Atom.setIdleTTL(0))
    : Atom.make(Effect.succeed(undefined)), [atoms, identity]);
  const result = useAtomValue(opened), descriptor = result._tag === "Success" ? result.value : undefined, session = descriptor?.identity === identity ? descriptor.session : undefined;
  const command = useMemo(() => atoms && session ? atoms.fn((input: { readonly action: () => void }) => session.schedule(input.action), { concurrent: true }) : Atom.fn((_: { readonly action: () => void }) => Effect.void), [atoms, session]);
  const dispatch = useAtomSet(command);
  return (action: () => void) => { if (descriptor?.identity === identity && descriptor.owner.state._tag !== "Closed") dispatch({ action }); };
}
export function useCommands(): readonly SurfaceCommand[] {
  const root = useContext(CommandsContext);
  const empty = useMemo(() => Atom.make(Effect.succeed([] as readonly SurfaceCommand[])), []);
  const state = useAtomValue(root?.commands.snapshot ?? empty);
  return state._tag === "Success" ? state.value : [];
}
export function useSurfaceCommands(surface: string, commands: readonly SurfaceCommand[]): void {
  const root = useContext(CommandsContext);
  const registration = useMemo(() => root ? root.atoms.atom(Effect.gen(function* () {
    const registry = yield* CommandRegistry;
    const cleanup = yield* registry.register(surface, commands);
    yield* Effect.addFinalizer(() => cleanup);
  })) : Atom.make(Effect.void), [root, surface, commands]);
  useAtomMount(registration);
}
